'use client';

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { getOrCreateCallId } from '@/lib/calls/getOrCreateCallId';
import {
  ToolSharingState,
  ToolType,
  SharedItem,
  hydrateToolSharingState,
  seedImplicitBackground,
  bindToolSharingCall,
  isToolType,
} from '@/store/slices/toolSharingSlice';
import { RootState } from '@/store/store';
import { CALLER_DEFAULT_UPLOAD, mapLegacyPath } from '@/lib/files/paths';
import { usePrivateFileGrant } from '@/contexts/FileGrantContext';
import { canPersistToolSharing } from '@/lib/callBackgrounds/toolSharingSelection';
import { projectDurableToolSharing, projectToolSharing } from '@/lib/files/fileGrantProtocol';

/**
 * Tool Sharing Real-Time Sync Hook
 *
 * Provides real-time Supabase sync for tool sharing state.
 * - Debounced writes (500ms) to prevent rate limiting
 * - Hydrates from Supabase on mount
 * - Last-write-wins conflict resolution with timestamps
 *
 * Pattern follows useNotesSync.ts
 */

const SYNC_DEBOUNCE_MS = 500;

interface UseToolSharingSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Custom debounce time in ms */
  debounceMs?: number;
}

interface ToolSharingSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
}

// Database schema for tool sharing content
interface DbToolSharingContent {
  shared_tools: ToolType[];
  background_revoked_at?: number;
  shared_files: string[];
  shared_items: SharedItem[];
  caller_read_only: boolean;
  caller_upload_folder: string;
  file_browser_access_token?: string | null;
  last_updated: number;
}

/**
 * Convert Redux ToolSharingState to DB schema format
 */
export function toDbFormat(state: ToolSharingState, selectedV2 = false): DbToolSharingContent {
  const durable = projectDurableToolSharing(state, { selectedV2 })!;
  return projectToolSharing({
    shared_tools: durable.sharedTools,
    background_revoked_at: durable.backgroundRevokedAt ?? 0,
    shared_files: durable.sharedFiles,
    shared_items: durable.sharedItems,
    caller_read_only: durable.callerReadOnly,
    caller_upload_folder: durable.callerUploadFolder,
    file_browser_access_token: durable.fileBrowserAccessToken,
    last_updated: durable.lastUpdated,
  }, selectedV2);
}

/**
 * Convert DB format back to Redux ToolSharingState (partial - fields we persist)
 */
export function fromDbFormat(content: DbToolSharingContent, selectedV2 = false): Partial<ToolSharingState> {
  return projectDurableToolSharing({
    sharedTools: (content.shared_tools || []).filter(isToolType),
    backgroundRevokedAt: typeof content.background_revoked_at === 'number' && Number.isFinite(content.background_revoked_at) && content.background_revoked_at >= 0 ? content.background_revoked_at : 0,
    sharedFiles: content.shared_files || [],
    sharedItems: content.shared_items || [],
    callerReadOnly: content.caller_read_only || false,
    callerUploadFolder: mapLegacyPath(content.caller_upload_folder || CALLER_DEFAULT_UPLOAD),
    fileBrowserAccessToken: selectedV2 ? null : content.file_browser_access_token || null,
    lastUpdated: content.last_updated || 0,
    // Derive these from sharedTools
    isSharing: (content.shared_tools || []).filter(isToolType).length > 0,
    activeTool: (content.shared_tools || []).filter(isToolType)[0] ?? null,
  }, { selectedV2 })!;
}

/**
 * Hash the complete tool selection plus runtime intent provenance. Provenance
 * participates in dirty checks, but is never serialized to storage. Shared by
 * the debounced writer (skip
 * redundant syncs) AND the reconnect dirty-guard (#725) so both hash the exact
 * same shape. NOTE: the local-only `tokenMintRequestCounter` is intentionally
 * excluded (it is never persisted); the forced-reload path preserves it
 * separately so a pending mint isn't lost.
 */
function hashToolSharing(s: ToolSharingState): string {
  return JSON.stringify({
    tools: s.sharedTools,
    selectionIntent: s.selectionIntent ?? 'explicit',
    backgroundRevokedAt: s.backgroundRevokedAt ?? 0,
    files: s.sharedFiles,
    items: s.sharedItems,
    ro: s.callerReadOnly,
    folder: s.callerUploadFolder,
    token: s.fileGrantMode?.version === 2 ? null : s.fileBrowserAccessToken,
    updated: s.lastUpdated,
  });
}

/**
 * Hook to sync tool sharing state to Supabase in real-time
 */
export function useToolSharingSync(
  options: UseToolSharingSyncOptions,
): ToolSharingSyncState & { reloadFromSupabase: () => Promise<void> } {
  const { callId, enabled = true, debounceMs = SYNC_DEBOUNCE_MS } = options;
  const privateFileGrant = usePrivateFileGrant();
  const selectedV2 = privateFileGrant.selected;

  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();

  const toolSharingState = useSelector((state: RootState) => state.toolSharing);

  // Live mirror of current Redux tool-sharing so the reconnect dirty-guard (#725)
  // can read it (incl. tokenMintRequestCounter) without widening deps.
  // Async reads must observe a dispatch even before React commits a rerender.
  const toolSharingRef = { get current() { return store.getState().toolSharing; } };

  // Refs for tracking state without triggering re-renders
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedHashRef = useRef<string | null>(null);
  const dbCallIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const stateRef = useReactiveRef<ToolSharingSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
  });

  // One committed call lifetime owns every read, debounce and completion.
  // Neither a late prior-call read nor a read failure is an unsaved new call.
  const scopeRef = useRef({ callId, enabled, ready: false, loadSerial: 0 });
  useLayoutEffect(() => {
    dispatch(bindToolSharingCall(callId));
    const scope = { callId, enabled, ready: false, loadSerial: 0 };
    scopeRef.current = scope;
    hasLoadedRef.current = false;
    dbCallIdRef.current = null;
    lastSyncedHashRef.current = null;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    stateRef.current = { isLoading: enabled, isSyncing: false, lastSyncTime: null, error: null, dbCallId: null };
    return () => { scope.ready = false; };
  }, [callId, enabled]);

  /**
   * Get or create the DB call ID (UUID) for this call
   */
  const getOrCreateDbCallId = useCallback(async (): Promise<string | null> => {
    if (dbCallIdRef.current) {
      return dbCallIdRef.current;
    }

    const scope = scopeRef.current;
    try {
      const dbCallId = await getOrCreateCallId(supabase, callId, {
        userId: user?.id ?? null,
      });
      if (scopeRef.current !== scope || !mountedRef.current) return null;
      if (dbCallId) {
        dbCallIdRef.current = dbCallId;
      }
      return dbCallId;
    } catch (err: unknown) {
      logger.error('useToolSharingSync', 'Failed to get/create call ID', { error: err, callId });
      return null;
    }
  }, [callId, supabase, user?.id]);

  /**
   * Sync tool sharing state to Supabase
   */
  const syncToSupabase = useCallback(async (state: ToolSharingState) => {
    if (!enabled || !callId || state.selectionCallId !== callId || !scopeRef.current.ready || !canPersistToolSharing(state)) return;
    const scope = scopeRef.current;
    const current = () => mountedRef.current && scopeRef.current === scope && scope.ready;

    // Compute hash to avoid redundant syncs
    const hash = hashToolSharing(state);

    if (hash === lastSyncedHashRef.current) {
      logger.debug('useToolSharingSync', 'Skipping sync - no changes');
      return;
    }

    stateRef.current = { ...stateRef.current, isSyncing: true };

    try {
      const dbCallId = await getOrCreateDbCallId();
      if (!dbCallId) {
        throw new Error('Failed to get/create call ID');
      }

      if (!current()) return;
      if (hashToolSharing(toolSharingRef.current) !== hash) {
        stateRef.current = { ...stateRef.current, isSyncing: false };
        return;
      }
      // Convert to DB format
      const dbContent = toDbFormat(state, selectedV2);

      // Upsert tool sharing state
      const { error } = await supabase
        .from('tool_sharing')
        .upsert({
          call_id: dbCallId,
          content: dbContent,
        }, {
          onConflict: 'call_id',
        });

      if (error) {
        throw error;
      }

      if (!current()) return;
      lastSyncedHashRef.current = hash;
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        lastSyncTime: Date.now(),
        error: null,
        dbCallId,
      };

      logger.debug('useToolSharingSync', 'Synced tool sharing to Supabase', {
        callId,
        sharedTools: state.sharedTools,
        sharedItemsCount: state.sharedItems.length,
      });
    } catch (err: unknown) {
      if (!current()) return;
      logger.error('useToolSharingSync', 'Failed to sync to Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, getOrCreateDbCallId, supabase, selectedV2]);

  /**
   * Load tool sharing state from Supabase (on mount)
   */
  const loadFromSupabase = useCallback(async (opts?: { force?: boolean }) => {
    if (!enabled || !callId || (!opts?.force && hasLoadedRef.current)) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    // #725 reconnect dirty-guard: on a FORCED re-sync, preserve the operator's
    // un-flushed sharing change rather than clobbering it with DB. The pending
    // debounced write flushes it. null baseline → never loaded → proceed (Obs1).
    if (
      opts?.force &&
      canPersistToolSharing(toolSharingRef.current) &&
      lastSyncedHashRef.current !== null &&
      hashToolSharing(toolSharingRef.current) !== lastSyncedHashRef.current
    ) {
      logger.info('useToolSharingSync', 'Skipping reconnect re-sync — un-flushed local sharing change pending');
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    const scope = scopeRef.current;
    const loadSerial = ++scope.loadSerial;
    scope.ready = false;
    const current = () => mountedRef.current && scopeRef.current === scope && scope.loadSerial === loadSerial;
    const startingState = toolSharingRef.current;
    const startingHash = hashToolSharing(startingState);
    const unchanged = () => hashToolSharing(toolSharingRef.current) === startingHash;
    const finish = (dbCallId: string | null) => {
      if (!current()) return;
      hasLoadedRef.current = true;
      scope.ready = true;
      stateRef.current = { ...stateRef.current, isLoading: false, dbCallId, error: null };
    };
    const seedIfNeverSaved = () => {
      const latest = toolSharingRef.current;
      if (latest.selectionIntent === 'unresolved' && latest.sharedTools.length === 0 && latest.toolCapabilities?.background === true) {
        dispatch(seedImplicitBackground());
      }
    };

    try {
      const { data: callData, error: callError } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!current()) return;
      if (callError) throw callError;
      if (!callData?.id) {
        seedIfNeverSaved();
        finish(null);
        return;
      }

      dbCallIdRef.current = callData.id;

      const { data: toolSharingData, error } = await supabase
        .from('tool_sharing')
        .select('content')
        .eq('call_id', callData.id)
        .maybeSingle();

      if (!current()) return;
      if (error) throw error; // maybeSingle reports genuine absence as data:null/error:null.
      if (toolSharingData && !toolSharingData.content) throw new Error('Saved tool sharing content is unavailable');

      // Before this hook has a saved baseline, a positive current selection
      // (local action, accepted peer or full-state restore) wins over this read.
      // This includes Retry after a read error and an explicit empty snapshot
      // whose saved timestamp is zero.
      const keepAcceptedSelection = lastSyncedHashRef.current === null && toolSharingRef.current.selectionCallId === callId && canPersistToolSharing(toolSharingRef.current);
      if (toolSharingData?.content && !keepAcceptedSelection && (unchanged() || !canPersistToolSharing(toolSharingRef.current))) {
        const content = toolSharingData.content as DbToolSharingContent;
        const dbState = fromDbFormat(content, selectedV2);

        // Build full state with defaults
        const fullState: ToolSharingState = {
          selectionIntent: 'explicit',
          selectionCallId: callId,
          toolCapabilities: toolSharingRef.current.toolCapabilities,
          sharedTools: dbState.sharedTools || [],
          backgroundRevokedAt: dbState.backgroundRevokedAt ?? 0,
          activeTool: dbState.activeTool ?? (dbState.sharedTools?.[0] || null),
          isSharing: dbState.isSharing ?? (dbState.sharedTools?.length || 0) > 0,
          callerReadOnly: dbState.callerReadOnly ?? false,
          sharedFiles: dbState.sharedFiles || [],
          sharedItems: dbState.sharedItems || [],
          callerUploadFolder: dbState.callerUploadFolder || CALLER_DEFAULT_UPLOAD,
          fileBrowserAccessToken: toolSharingRef.current.fileBrowserAccessToken !== startingState.fileBrowserAccessToken
            ? toolSharingRef.current.fileBrowserAccessToken : dbState.fileBrowserAccessToken || null,
          callerHasMinimized: false,
          callerActiveTab: 'chat',
          newlySharedTools: [],
          lastUpdated: dbState.lastUpdated || 0,
          // Local-only counter; not persisted in DB. Reset to 0 on a MOUNT
          // hydrate so a stale DB row can't synthesize a phantom mint request,
          // but PRESERVE the live counter on a forced reconnect reload (#725)
          // so an in-session token mint isn't silently dropped.
          tokenMintRequestCounter: opts?.force || toolSharingRef.current.tokenMintRequestCounter !== startingState.tokenMintRequestCounter
            ? toolSharingRef.current.tokenMintRequestCounter : 0,
        };

        dispatch(hydrateToolSharingState(fullState));
        lastSyncedHashRef.current = hashToolSharing(fullState);

        logger.info('useToolSharingSync', 'Loaded from Supabase', {
          sharedTools: fullState.sharedTools,
          sharedItemsCount: fullState.sharedItems.length,
        });
      }

      if (!toolSharingData) seedIfNeverSaved();
      // A local edit during the read wins. Preserve a baseline for #725's
      // dirty guard, then let the existing writer persist the current choice.
      if (!unchanged() && lastSyncedHashRef.current === null) lastSyncedHashRef.current = startingHash;
      finish(callData.id);
    } catch (err: unknown) {
      if (!current()) return;
      scope.ready = false;
      hasLoadedRef.current = false;
      logger.error('useToolSharingSync', 'Failed to load from Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, supabase, dispatch, selectedV2]);

  // #725: force a re-sync from Supabase on LiveKit reconnect (dirty-guard above
  // preserves an un-flushed local sharing change + the token-mint counter).
  // isReloadingRef collapses back-to-back reconnects into one in-flight fetch.
  const isReloadingRef = useRef<object | null>(null);
  const reloadFromSupabase = useCallback(async () => {
    const scope = scopeRef.current;
    if (isReloadingRef.current === scope) return;
    isReloadingRef.current = scope;
    try {
      await loadFromSupabase({ force: true });
    } finally {
      if (isReloadingRef.current === scope) isReloadingRef.current = null;
    }
  }, [loadFromSupabase]);

  // Load from Supabase on mount
  useEffect(() => {
    if (!enabled || authLoading) return;

    loadFromSupabase();
  }, [enabled, authLoading, loadFromSupabase]);

  // Read recovery can admit an unchanged explicit selection. Its readiness
  // transition must rerun this effect even when isLoading was already false.
  const syncReady = scopeRef.current.ready;

  // Debounced sync when tool sharing state changes
  useEffect(() => {
    if (!enabled || !callId || !scopeRef.current.ready || !canPersistToolSharing(toolSharingState)) return;

    // Cancel pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync
    syncTimeoutRef.current = setTimeout(() => {
      syncToSupabase(toolSharingState);
    }, debounceMs);
  }, [enabled, callId, toolSharingState, debounceMs, syncToSupabase, stateRef.current.isLoading, syncReady]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return { ...stateRef.current, reloadFromSupabase };
}

/**
 * Hook to hydrate tool sharing state from storage
 */
export function useToolSharingHydration(callId: string): {
  hydrate: () => Promise<void>;
  isHydrated: boolean;
} {
  const selectedV2 = usePrivateFileGrant().selected;
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const supabase = createClient();
  const hydratedRef = useRef(false);
  const hydrationCallRef = useRef({callId});
  const hydrationState = useSelector((state: RootState) => state.toolSharing);
  const hydrationStateRef = useRef(hydrationState);
  hydrationStateRef.current = hydrationState;
  const mounted = useRef(true);
  useLayoutEffect(() => { hydrationCallRef.current = {callId}; hydratedRef.current = false; }, [callId]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const hydrate = useCallback(async () => {
    if (hydratedRef.current || !callId) return;

    const ownedScope = hydrationCallRef.current;
    const startingHash = hashToolSharing(hydrationStateRef.current);
    const current = () => mounted.current && hydrationCallRef.current === ownedScope;
    try {
      const { data: callData, error: callError } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!current()) return;
      if (callError) throw callError;
      if (callData?.id) {
        const { data: toolSharingData, error: sharingError } = await supabase
          .from('tool_sharing')
          .select('content')
          .eq('call_id', callData.id)
          .maybeSingle();

        if (!current()) return;
        if (sharingError) throw sharingError;
        if (toolSharingData?.content && hashToolSharing(hydrationStateRef.current) === startingHash) {
          const content = toolSharingData.content as DbToolSharingContent;
          const dbState = fromDbFormat(content, selectedV2);
          const fullState: ToolSharingState = {
            sharedTools: dbState.sharedTools || [],
          backgroundRevokedAt: dbState.backgroundRevokedAt ?? 0,
            activeTool: dbState.activeTool ?? (dbState.sharedTools?.[0] || null),
            isSharing: dbState.isSharing ?? (dbState.sharedTools?.length || 0) > 0,
            callerReadOnly: dbState.callerReadOnly ?? false,
            sharedFiles: dbState.sharedFiles || [],
            sharedItems: dbState.sharedItems || [],
            callerUploadFolder: dbState.callerUploadFolder || CALLER_DEFAULT_UPLOAD,
            fileBrowserAccessToken: dbState.fileBrowserAccessToken || null,
            callerHasMinimized: false,
            callerActiveTab: 'chat',
            newlySharedTools: [],
            lastUpdated: dbState.lastUpdated || 0,
            // Local-only counter; not persisted in DB. Reset on hydrate
            // so a stale DB row can't synthesize a phantom mint request.
            tokenMintRequestCounter: 0,
          };
          dispatch(hydrateToolSharingState(fullState));
          logger.info('useToolSharingHydration', 'Hydrated from Supabase', {
            sharedTools: fullState.sharedTools,
          });
        }
      }

      if (current()) hydratedRef.current = true;
    } catch (err: unknown) {
      if (!current()) return;
      logger.error('useToolSharingHydration', 'Hydration failed', { error: err });
    }
  }, [callId, dispatch, supabase, selectedV2]);

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useToolSharingSync;
